import React from 'react';
import PropTypes from 'prop-types';
import {DragDropContext} from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import NProgress from 'react-nprogress';
import * as Actions from 'common/actions/actions';
import DropdownMenu from 'common/components/dropdown-menu';
import {TooltipContext} from 'charts/components/tooltip';
import HelptextTooltip from 'common/components/helptext-tooltip';
import * as TooltipType from 'common/enum/tooltip-type';
import {Layout} from 'common/components/icon';
import ReportCard from 'common/components/report-card';
import ShadedBackground from 'common/components/shaded-background';
import Dispatcher from 'common/events/dispatcher';
import {ReportLayoutEnum} from 'common/proxies/enum-proxy';
import Button from 'common/components/button';
import Portal from 'common/components/portal';
import ReportsDashboardStore from 'common/stores/reports-dashboard-store';
import {htmlUnescape} from 'common/utils/string-util';
import {sort} from 'common/utils/array-util';
import SvgSymbol from 'common/components/svg-symbol';
import {t} from 'common/utils/translation-util';
import AddCard from './add-card';
import DashboardErrorCard from './dashboard-error-card';
import DragLayer from './drag-layer';
import DraggableCard from './draggable-card';
import Dropzone from './dropzone';
import ZeroCard from './zero-card';
import Colors from 'common/constants/colors';
import './index.scss';

class Dashboard extends React.Component {
  static defaultProps = {
    dashboard: {
      id: -1,
    },
    hasBackground: true,
    hasMinHeight: true,
    hasPadding: true,
    inStoryboard: false,
    readonly: false,
    report: {},
    showTitle: false,
    style: {},
    titleStyle: {},
  };

  static propTypes = {
    dashboard: PropTypes.object.isRequired,
    hasBackground: PropTypes.bool,
    hasMinHeight: PropTypes.bool,
    hasPadding: PropTypes.bool,
    inStoryboard: PropTypes.bool,
    readonly: PropTypes.bool,
    report: PropTypes.object,
    showTitle: PropTypes.bool,
    storyboardChapterId: PropTypes.number,
    style: PropTypes.object,
    titleStyle: PropTypes.object,
    userCanEditDashboard: PropTypes.bool.isRequired,
  };

  // todo set refresh timer in this class
  constructor(props) {
    super(props);

    [
      'findCard',
      'getContainerBounds',
      'renderLaneSwitcher',
      'renderLayoutSwitcher',
      'submitLane',
      'submitLayout',
      '_onChange',
      '_onError',
    ].forEach((method) => (this[method] = this[method].bind(this)));

    this.state = {
      columns: [],
      liveUpdateIntervalId: null,
      laneDirection: props.dashboard.laneDirection.id,
      layoutEnum: {},
      layout: 0,
      loading: true,
      rows: [],
    };

    NProgress.start();

    this.urls = {
      actionEdit: window.settings.urls.actionEdit.replace(
        0,
        props.dashboard.id,
      ),
    };
  }

  UNSAFE_componentWillMount() {
    const data = window.settings.dashboard.dashboardData || this.props.report;
    if (data && Object.keys(data).length > 0) {
      Dispatcher.dispatch({
        action: 'UPDATE_REPORTS_DASHBOARD',
        reports: data,
      });
      this._onChange();
    } else {
      this.loadReports();
    }
  }

  componentDidMount() {
    ReportsDashboardStore.addChangeListener(this._onChange);
    ReportsDashboardStore.addErrorListener(this._onError);
    this.initModals(this.props.readonly);

    if (this.props.readonly && !this.props.userCanEditDashboard) {
      this.readonlyDashboardIntervalId = setInterval(
        () => {
          this.loadReports(false);
        },
        15 * 60 * 1_000,
      );
    }
  }

  componentWillUnmount() {
    ReportsDashboardStore.removeChangeListener(this._onChange);
    ReportsDashboardStore.removeErrorListener(this._onError);
    if (this.readonlyDashboardIntervalId) {
      clearInterval(this.readonlyDashboardIntervalId);
    }
    if (this.state.liveUpdateIntervalId) {
      clearInterval(this.state.liveUpdateIntervalId);
    }
  }

  getContainerBounds() {
    return this._dashboard.getBoundingClientRect();
  }

  getFormattedPositions(columns) {
    const pluckReportId = (result, report) => result.concat([report.id]);
    const columnsReducer = (result, column) =>
      result.concat([column.reports.reduce(pluckReportId, [])]);
    return columns.reduce(columnsReducer, []);
  }

  /**
   * Calculates how many seconds we should wait between live updates
   *   of this dashboard.
   * @param {[meta: { refreshSeconds: number | undefined}]} reports
   * @returns the minimum configured interval time across all
   *   reports in this dashboard
   */
  getRefreshIntervalInSeconds(reports) {
    /**
     * Math.min() returns 0 in the following unfortunate cases:
     * - Math.min([])
     * - Math.min([undefined])
     *
     * Math.min() returns NaN when there exists more than 1 entry
     *   in the array, but at least one of them is `null` or
     *   `undefined`
     */
    const refreshIntervalsInSeconds = reports
      .map((report) => report.meta.refreshSeconds)
      .filter((interval) => typeof interval === 'number');

    if (refreshIntervalsInSeconds.length === 0) {
      return null;
    }

    return Math.min(...refreshIntervalsInSeconds);
  }

  setLayout(layout) {
    const widths = layout.widths.split(' ');
    const old = this.state.columns;
    const columns = widths.map((width, index) => ({
      width,
      col: index,
      reports: index < old.length ? old[index].reports : [],
    }));

    if (old.length > columns.length) {
      const last = columns.length - 1;
      for (let ii = columns.length, nn = old.length; ii < nn; ii++) {
        columns[last].reports = columns[last].reports.concat(old[ii].reports);
        [].push.apply(columns[last], old[ii].reports);
      }
    }
    const rows = this._getRows(columns, layout);

    this.emitSetLayoutEvent();
    this.setState({columns, layout, rows});
    this.submitLayout(layout.id, columns);
  }

  setLane(orientation) {
    this.setState({laneDirection: orientation});
    this.submitLane(orientation);
  }

  _onError() {
    this.setState({
      columns: [],
      error: ReportsDashboardStore.getError(),
      loading: false,
    });

    NProgress.done();
  }

  _onChange() {
    const reports = ReportsDashboardStore.getReports();
    const meta = ReportsDashboardStore.getMeta();

    const layoutId = meta.layoutId || 0;

    // Sort layouts by id to maintain preferred order
    const layouts = sort.ascending(Object.values(ReportLayoutEnum), 'id');

    const layout = layouts.filter((layoutObj) => {
      return layoutObj.id === layoutId;
    })[0];

    const widths = layout.widths.split(' ');
    const columns = widths.map((datum, index) => ({
      width: widths[index],
      reports: [],
    }));

    reports.data.forEach((report) => {
      // handle escaped characters in exports
      if (Array.isArray(report.data)) {
        for (const datum of report.data) {
          if ('name' in datum) {
            datum['name'] = htmlUnescape(datum['name']);
          }
        }
      }

      let x = report.meta ? report.meta.x || 0 : 0;
      const numColumns = widths.length;
      if (x >= numColumns - 1) {
        x = numColumns - 1;
      }
      columns[x].reports.push(report);
    });

    const rows = this._getRows(columns, layout);

    let liveUpdateIntervalId = this.state.liveUpdateIntervalId;
    const refreshIntervalInSeconds = this.getRefreshIntervalInSeconds(
      reports.data,
    );

    const isLive = typeof refreshIntervalInSeconds === 'number';

    if (isLive && !liveUpdateIntervalId) {
      liveUpdateIntervalId = setInterval(
        () => this.loadReports(false),
        refreshIntervalInSeconds * 1_000,
      );
    } else if (!isLive && liveUpdateIntervalId) {
      clearInterval(liveUpdateIntervalId);
      liveUpdateIntervalId = null;
    }

    this.setState({
      isDefault: meta.default,
      columns,
      error: null,
      layout,
      layoutEnum: layouts,
      liveUpdateIntervalId,
      loading: false,
      rows,
    });

    NProgress.done();
  }

  _getRows(columns, layout) {
    // reports in each row are a sparse array with nulls for blanks
    const widths = layout.widths.split(' ');
    const rows = [];
    let maxY = -1;
    const maxYbyCol = {0: 0, 1: 0, 2: 0};
    const reportsByCoords = {};
    columns.forEach((col, x) => {
      col.reports.forEach((report, y) => {
        maxY = Math.max(y, maxY);
        maxYbyCol[x] = y;
        reportsByCoords[[x, y]] = report;
        if (rows[y] === undefined) {
          rows[y] = {
            widths,
            reports: [],
            showAdd: [],
          };
        }
      });
    });

    if (maxY >= 0) {
      for (const x of Array(widths.length).keys()) {
        let addDone = false;
        for (const y of Array(maxY + 1).keys()) {
          let report = null;
          if ([x, y] in reportsByCoords) {
            report = reportsByCoords[[x, y]];
          }
          rows[y].reports.push(report);

          if (addDone) {
            continue;
          }
          // when to show add buttons:
          // empty row, last in longest column, after last in short column
          if (!report && y === 0 && maxYbyCol[x] === 0) {
            rows[y].showAdd[x] = true;
            addDone = true;
          } else if (report && y === maxY) {
            rows[y].showAdd[x] = true;
            addDone = true;
          } else if (!report && maxY >= y) {
            rows[y].showAdd[x] = true;
            addDone = true;
          } else {
            rows[y].showAdd[x] = false;
          }
        }
      }
    }

    return rows;
  }

  emitSetLayoutEvent() {
    const event = document.createEvent('Event');
    event.initEvent('Dashboard-setLayout', true, false);
    window.dispatchEvent(event);
  }

  beginDrag(colWidth, draggedColIndex, draggedIndex) {
    this.setState({
      colWidth,
      draggedColIndex,
      draggedIndex,
      dropAfter: null,
      dropBefore: null,
      dropColumn: null,
    });
  }

  dropAt(colIndex, index) {
    const {draggedColIndex, draggedIndex, columns} = this.state;
    const card = columns[draggedColIndex].reports.splice(draggedIndex, 1)[0];
    columns[colIndex].reports.splice(index, 0, card);
    const rows = this._getRows(columns, this.state.layout);
    this.setState({columns, dropped: true, rows});
    this.submitLayout(this.state.layout.id, columns);
  }

  dropzone(dropColumn, dropBefore, dropAfter) {
    if (
      this.state.dropColumn !== dropColumn ||
      this.state.dropBefore !== dropBefore ||
      this.state.dropAfter !== dropAfter
    ) {
      this.setState({dropAfter, dropBefore, dropColumn});
    }
  }

  findCard(id) {
    return this.state.columns.reduce((result, column, colIndex) => {
      const filtered = column.reports
        .map((report, index) => ({report, colIndex, index}))
        .filter((datum) => datum.report.id === id);
      return filtered.length === 1 ? filtered[0] : result;
    }, {});
  }

  initModals(readonly) {
    if (readonly) {
      return;
    }

    const self = this;
    $('#createReport').ajax_modal({
      url: window.settings.dashboard.createReportUrl,
      shown() {
        const col = $(this).data('col');
        this.find('form')
          .append(
            $('<input>')
              .attr('type', 'hidden')
              .attr('name', 'x')
              .attr('value', col),
          )
          .append(
            $('<input>')
              .attr('type', 'hidden')
              .attr('name', 'y')
              .attr('value', self.state.columns[col].reports.length + 1),
          );
      },
      redirect: this.loadReports.bind(this),
    });

    $('#saveReport').ajax_modal({
      url() {
        // If the duplicate button was clicked, use the duplicate URL
        // Otherwise use the save URL
        const objId = $(this).data('obj_id');
        const urlTemplate = $(this).data('duplicate')
          ? window.settings.dashboard.duplicateReportUrlTemplate
          : window.settings.dashboard.saveReportUrlTemplate;
        return urlTemplate.replace('/0/', `/${objId}/`);
      },
      redirect: this.loadReports.bind(this),
    });

    $('#copyCardToModal').ajax_modal({
      url() {
        const objId = $(this).data('obj_id');
        const urlTemplate =
          window.settings.dashboard.copyCardToDashboardModalUrl;
        return urlTemplate.replace('/0/', `/${objId}/`);
      },
      shown() {
        const modal = $(this);

        modal.find('#id_dashboard').select2({
          width: 200,
          dropdownAutoWidth: true,
        });
      },
      success: window.location.reload, // for success message
    });

    $('#deleteReportModal').ajax_modal({
      url() {
        // FIXME Buried global
        return window.settings.dashboard.deleteReportUrlTemplate.replace(
          '/0/',
          `/${$(this).data('report_id')}/`,
        );
      },
      success: () => {
        $('#deleteReportModal').modal('hide');
        this.loadReports();
      },
    });

    $('#shareReportModal').ajax_modal({
      url() {
        // FIXME Buried global
        return window.settings.dashboard.shareReportModalUrl.replace(
          '/0/',
          `/${$(this).data('report_id')}/`,
        );
      },
      success: () => {
        $('#shareReportModal').modal('hide');
        this.loadReports();
      },
    });

    $(document)
      .on('click', '.createReport', function () {
        $('#createReport')
          .data({col: $(this).data('col')})
          .modal('show');
      })
      .on('click', '.cardConfigModal', function () {
        $('#saveReport')
          .data({obj_id: $(this).data('obj_id'), duplicate: false})
          .modal('show');
      })
      .on('click', '.deleteReport', function () {
        $('#deleteReportModal')
          .data({report_id: $(this).data('report_id')})
          .modal('show');
      })
      .on('click', '.duplicateReport', function () {
        $('#saveReport')
          .data({obj_id: $(this).data('obj_id'), duplicate: true})
          .modal('show');
      })
      .on('click', '.copyCardTo', function () {
        $('#copyCardToModal')
          .data({obj_id: $(this).data('obj_id')})
          .modal('show');
      })
      .on('click', '.shareReport', function () {
        $('#shareReportModal')
          .data({report_id: $(this).data('report_id')})
          .modal('show');
      });
  }

  loadReports(displayLoadingSpinner = true) {
    this.setState({loading: displayLoadingSpinner});
    if (this.props.storyboardChapterId) {
      Actions.getReportsDashboardByChapter(this.props.storyboardChapterId);
    } else {
      Actions.getReportsDashboard(this.props.dashboard.uuid);
    }
    if (window.settings.user.isAuthenticated) {
      $.get(window.settings.dashboard.messagesJsonUrl, (result) => {
        $(result || []).each((index, msg) => {
          toastr[msg[0]](msg[1]);
        });
      });
    }
  }

  submitLane(orientation) {
    const url = window.settings.dashboard.laneDirectionUrl.replace(
      0,
      this.props.dashboard.id,
    );
    return $.post(
      url,
      {
        lane_direction: orientation,
        csrfmiddlewaretoken: window.settings.csrf,
      },
      () => {
        // success
      },
    ).fail(() => {
      // TODO: handle errors here
    });
  }

  submitLayout(layoutId, columns) {
    const parent = `${window.settings.dashboard.objectTypeId}_${this.props.dashboard.id}`;
    return $.post(
      `/reports/position/?parent=${parent}`,
      {
        reports: JSON.stringify(this.getFormattedPositions(columns)),
        layout: layoutId,
        csrfmiddlewaretoken: window.settings.csrf,
      },
      () => {
        this.setState({dropped: false});
      },
    ).fail(() => {
      // TODO: handle errors here
    });
  }

  renderColumn(forExport, showAddButton, col, colIndex) {
    const canEditCards = window.settings.dashboard.canEditCards;
    return (
      <TooltipContext.Provider
        key={colIndex}
        value={{getContainerEdges: this.getContainerBounds}}>
        <div className={'DashboardColumn'} style={{width: col.width}}>
          {col.reports.map((report, rowIndex) => [
            this.state.dropBefore === rowIndex &&
            this.state.dropColumn === colIndex ? (
              <Dropzone
                drop={this.dropAt.bind(this, colIndex, rowIndex)}
                key={`dropzone-${colIndex}`}
              />
            ) : null,

            this.props.readonly || !canEditCards || this.state.isDefault ? (
              <ReportCard
                disableConfig={
                  this.props.readonly || !this.props.userCanEditDashboard
                }
                forExport={forExport}
                inStoryboard={this.props.inStoryboard}
                key={report.id}
                report={report}
              />
            ) : (
              <DraggableCard
                beginDrag={this.beginDrag.bind(this, col.width, colIndex)}
                disableConfig={!this.props.userCanEditDashboard}
                dropzone={this.dropzone.bind(this, colIndex)}
                findCard={this.findCard}
                forExport={forExport}
                id={report.id}
                key={report.id}
                report={report}
              />
            ),

            this.state.dropAfter === rowIndex &&
            this.state.dropColumn === colIndex ? (
              <Dropzone drop={this.dropAt.bind(this, colIndex, rowIndex + 1)} />
            ) : null,
          ])}

          {col.reports.length === 0 &&
          this.state.dropAfter === -1 &&
          this.state.dropColumn === colIndex ? (
            <Dropzone drop={this.dropAt.bind(this, colIndex, 0)} />
          ) : null}

          {showAddButton && !this.state.loading && canEditCards ? (
            <AddCard
              colIndex={colIndex}
              dropzone={this.dropzone.bind(this, colIndex)}
              findCard={this.findCard}
              reportsLength={col.reports.length}
            />
          ) : null}
        </div>
      </TooltipContext.Provider>
    );
  }

  renderRow(forExport, showAddButton, row, rowIndex) {
    const canEditCards = window.settings.dashboard.canEditCards;
    return (
      <TooltipContext.Provider
        key={rowIndex}
        value={{getContainerEdges: this.getContainerBounds}}>
        <div className={'DashboardRow'}>
          {row.reports.map((report, colIndex) => {
            const width = row.widths[colIndex];

            return (
              <div className={'DashboardColumn'} key={colIndex} style={{width}}>
                {report ? (
                  <React.Fragment key={colIndex}>
                    {this.state.dropBefore === rowIndex &&
                    this.state.dropColumn === colIndex ? (
                      <Dropzone
                        drop={this.dropAt.bind(this, colIndex, rowIndex)}
                        key={`dropzone-${rowIndex}-${colIndex}`}
                      />
                    ) : null}

                    {this.props.readonly ||
                    !canEditCards ||
                    this.state.isDefault ? (
                      <ReportCard
                        disableConfig={
                          this.props.readonly ||
                          !this.props.userCanEditDashboard
                        }
                        forExport={forExport}
                        inStoryboard={this.props.inStoryboard}
                        key={report.id}
                        report={report}
                      />
                    ) : (
                      <DraggableCard
                        beginDrag={this.beginDrag.bind(this, width, colIndex)}
                        disableConfig={!this.props.userCanEditDashboard}
                        dropzone={this.dropzone.bind(this, colIndex)}
                        findCard={this.findCard}
                        forExport={forExport}
                        id={report.id}
                        key={report.id}
                        report={report}
                      />
                    )}

                    {this.state.dropAfter === rowIndex &&
                    this.state.dropColumn === colIndex ? (
                      <Dropzone
                        drop={this.dropAt.bind(this, colIndex, rowIndex + 1)}
                      />
                    ) : null}
                  </React.Fragment>
                ) : null}

                {rowIndex === 0 &&
                this.state.dropAfter === -1 &&
                this.state.dropColumn === colIndex ? (
                  <Dropzone drop={this.dropAt.bind(this, colIndex, 0)} />
                ) : null}

                {row.showAdd[colIndex] &&
                showAddButton &&
                !this.state.loading &&
                canEditCards ? (
                  <AddCard
                    colIndex={colIndex}
                    dropzone={this.dropzone.bind(this, colIndex)}
                    findCard={this.findCard}
                    reportsLength={rowIndex}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </TooltipContext.Provider>
    );
  }

  renderLaneSwitcher() {
    return (
      <Portal selector={'#dashboard-actions-lane'}>
        <Button
          alt={'Freeform'}
          className={'LaneSwitcher Button--left-edge'}
          isDisabled={this.state.laneDirection === 'vertical'}
          onClick={() => this.setLane('vertical')}>
          <HelptextTooltip
            icon={`dashboard-vertical`}
            tooltipType={TooltipType.laneSwitcher}>
            {t('Stack cards from top to bottom in freeform columns.')}
          </HelptextTooltip>
        </Button>
        <Button
          alt={'Fixed rows'}
          className={'LaneSwitcher Button--right-edge'}
          isDisabled={this.state.laneDirection === 'horizontal'}
          onClick={() => this.setLane('horizontal')}>
          <HelptextTooltip
            icon={`dashboard-horizontal`}
            tooltipType={TooltipType.laneSwitcher}>
            {t('Align cards from left to right in fixed rows.')}
          </HelptextTooltip>
        </Button>
      </Portal>
    );
  }

  renderLayoutSwitcher() {
    return this.props.readonly || this.state.isDefault ? null : (
      <Portal selector={'#dashboard-actions-layout'}>
        <Button
          className={'LayoutSwitcher Button--menu'}
          onClick={() => this.dropdownMenuRef.show()}>
          <Layout />
          <DropdownMenu
            ref={(dropdownMenu) => (this.dropdownMenuRef = dropdownMenu)}>
            {this.state.layoutEnum.map((layoutItem) => (
              <li
                className={'LayoutSwitcherItem'}
                key={layoutItem.id}
                onClick={this.setLayout.bind(this, layoutItem)}>
                <SvgSymbol
                  name={`layout-${layoutItem.slug.split('_').join('-')}`}
                />
                <span className={`DashboardLayout`}>
                  {layoutItem.widths.split(' ').map((el, idx) => (
                    <i key={`${layoutItem.id}-${idx}`} />
                  ))}
                  {layoutItem.displayName}
                </span>
              </li>
            ))}
          </DropdownMenu>
        </Button>
      </Portal>
    );
  }

  render() {
    const {forExport, fullscreen, dashboardName} = window.settings.dashboard;
    const {readonly, style, userCanEditDashboard} = this.props;
    const {
      columns,
      colWidth,
      dropped,
      isDefault,
      layout,
      loading,
      error,
      rows,
    } = this.state;
    const nonZeroColumns = columns.filter((col) => col.reports.length > 0);
    const displayZeroCard =
      !loading &&
      !error &&
      nonZeroColumns.length === 0 &&
      !isDefault &&
      userCanEditDashboard;
    const showAddButton =
      !readonly && !displayZeroCard && !isDefault && userCanEditDashboard;
    const displayLayoutSwitcher =
      !loading &&
      !forExport &&
      layout &&
      !readonly &&
      !displayZeroCard &&
      userCanEditDashboard;
    const displayLaneSwitcher =
      !isDefault &&
      !loading &&
      !forExport &&
      !readonly &&
      !displayZeroCard &&
      userCanEditDashboard;
    return (
      <div ref={(dashboard) => (this._dashboard = dashboard)} style={style}>
        {(forExport || fullscreen) && dashboardName ? (
          <h1>
            {htmlUnescape(dashboardName)} <img className={'OrgLogo'} />
          </h1>
        ) : null}

        <ShadedBackground
          style={{
            backgroundColor: this.props.hasBackground ? Colors.gray200 : null,
            minHeight: this.props.hasMinHeight ? null : '0px',
            padding: this.props.hasPadding ? null : '0px',
          }}>
          {displayLaneSwitcher ? this.renderLaneSwitcher() : null}
          {displayLayoutSwitcher ? this.renderLayoutSwitcher() : null}
          {displayZeroCard ? (
            <ZeroCard
              href={this.props.readonly ? this.urls.actionEdit : null}
              isHomepage={this.props.readonly}
            />
          ) : null}

          {error ? <DashboardErrorCard /> : null}

          <div
            className={`ContainerMain ContainerMain--no-padding${
              dropped ? ' ContainerMain--no-transition' : ''
            }`}>
            {this.props.showTitle && this.props.report.meta.name ? (
              <div className={'DashboardTitle'} style={this.props.titleStyle}>
                {this.props.report.meta.name}
              </div>
            ) : null}

            {this.state.laneDirection === 'horizontal'
              ? rows.map(this.renderRow.bind(this, forExport, showAddButton))
              : columns.map(
                  this.renderColumn.bind(this, forExport, showAddButton),
                )}
          </div>

          {forExport ? <div className={'FooterLogo'} /> : null}

          <DragLayer colWidth={colWidth} />
        </ShadedBackground>
      </div>
    );
  }
}

export default DragDropContext(HTML5Backend)(Dashboard);
